import React, { useState, useRef, useEffect } from 'react';
import { Camera, Mic, MicOff, CheckCircle, AlertCircle, Upload, RotateCcw, Ruler, Building, FileText } from 'lucide-react';
import ApiService from '../services/api';
import { saveToNocoDB } from '../utils/nocodb';
function PriseDeCote() {
  // États du formulaire
  const [formData, setFormData] = useState({
    marque: '',
    modele: '',
    cotes: '',
    notes: ''
  });
  
  // Photos
  const [photos, setPhotos] = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);
  
  // Audio
  const [audioBlob, setAudioBlob] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Dictée vocale
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const recognitionRef = useRef(null);
  
  // Envoi
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Initialisation reconnaissance vocale
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'fr-FR';
      
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        if (finalTranscript) {
          setFormData(prev => ({ 
            ...prev, 
            notes: prev.notes + (prev.notes ? ' ' : '') + finalTranscript 
          }));
        }
      };
      
      recognitionRef.current.onerror = () => setIsListening(false);
      recognitionRef.current.onend = () => setIsListening(false);
    } else {
      setSpeechSupported(false);
    }
    
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Toggle dictée
  const toggleDictation = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  // Gestion photos
  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setPhotos(prev => [...prev, ...files]);
      const urls = files.map(file => URL.createObjectURL(file));
      setPhotoPreviewUrls(prev => [...prev, ...urls]);
    }
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photoPreviewUrls[index]);
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Enregistrement audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setAudioDuration(0);
      
      timerRef.current = setInterval(() => {
        setAudioDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      alert('Impossible d\'accéder au micro : ' + err.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const deleteRecording = () => {
    setAudioBlob(null);
    setAudioDuration(0);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Mise à jour formulaire
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Conversion fichiers en base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Envoi vers NAS
  const handleSendToNAS = async () => {
    if (!formData.marque && !formData.modele && photos.length === 0 && !audioBlob) {
      setError('Veuillez remplir au moins un champ ou ajouter une photo/audio');
      return;
    }

    setSending(true);
    setError(null);
    setResult(null);

    try {
      // Convertir photos en base64
      const photoData = await Promise.all(
        photos.map(async (file) => ({
          name: file.name,
          type: file.type,
          data: await fileToBase64(file)
        }))
      );

      // Convertir audio en base64 si présent
      let audioData = null;
      if (audioBlob) {
        audioData = await blobToBase64(audioBlob);
      }

      const payload = {
        ...formData,
        date: new Date().toISOString(),
        photos: photoData,
        audio: audioData ? { name: 'message_vocal.webm', data: audioData } : null
      };

      // Envoyer vers NocoDB

      // Pour l'instant, on envoie vers Google Drive (NAS à configurer)
      const response = await ApiService.sendPriseDeCote(payload);
      
      setResult({
        success: true,
        driveLink: response.driveLink,
        folderId: response.folderId,
        message: 'Prise de côte enregistrée avec succès !'
      });

      // Reset formulaire
      setFormData({ marque: '', modele: '', cotes: '', notes: '' });
      photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setPhotos([]);
      setPhotoPreviewUrls([]);
      setAudioBlob(null);

    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  // Reset
  const handleReset = () => {
    if (window.confirm('Réinitialiser le formulaire ?')) {
      photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setPhotos([]);
      setPhotoPreviewUrls([]);
      setAudioBlob(null);
      setFormData({ marque: '', modele: '', cotes: '', notes: '' });
      setResult(null);
      setError(null);
    }
  };

  return (
    <div className="prise-cote-container">
      <h2 className="section-title"><Ruler size={24} /> Prise de Côte</h2>

      {result?.success && (
        <div className="alert success">
          <CheckCircle size={20} />
          <div>
            <p>{result.message}</p>
            {result.driveLink && (
              <p style={{ marginTop: 8 }}>
                <a href={result.driveLink} target="_blank" rel="noopener noreferrer" className="drive-link">
                  📁 Ouvrir le dossier
                </a>
              </p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="alert error">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      {/* Identification */}
      <div className="form-section">
        <h3><Building size={18} /> Identification</h3>
        <div className="form-row two-cols">
          <div className="form-group">
            <label>Marque</label>
            <input
              type="text"
              value={formData.marque}
              onChange={(e) => handleInputChange('marque', e.target.value)}
              placeholder="Ex: Fichet-Bauche"
              className="dark-input"
            />
          </div>
          <div className="form-group">
            <label>Modèle</label>
            <input
              type="text"
              value={formData.modele}
              onChange={(e) => handleInputChange('modele', e.target.value)}
              placeholder="Ex: Millium II 120"
              className="dark-input"
            />
          </div>
        </div>
      </div>

      {/* Côtes - Champ unique texte + chiffres */}
      <div className="form-section">
        <h3><FileText size={18} /> Côtes</h3>
        <div className="form-group full-width">
          <textarea
            value={formData.cotes}
            onChange={(e) => handleInputChange('cotes', e.target.value)}
            placeholder="Ex: Hauteur 1200mm x Largeur 800mm x Profondeur 600mm... ou toutes dimensions, notes..."
            rows={4}
            className="dark-textarea"
          />
        </div>
      </div>

      {/* Photos */}
      <div className="form-section">
        <h3><Camera size={18} /> Photos</h3>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={handlePhotoChange}
          id="photo-input"
          style={{ display: 'none' }}
        />
        <label htmlFor="photo-input" className="btn-primary-purple">
          <Camera size={20} />
          Ajouter photos
        </label>
        
        {photoPreviewUrls.length > 0 && (
          <div className="photos-list">
            {photoPreviewUrls.map((url, index) => (
              <div key={index} className="photo-item">
                <img src={url} alt={`Photo ${index + 1}`} />
                <button className="btn-remove" onClick={() => removePhoto(index)}>
                  <RotateCcw size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notes avec dictée */}
      <div className="form-section">
        <h3>Notes complémentaires</h3>
        <div className="notes-area">
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Décrivez l'état du coffre, les particularités, contraintes d'accès..."
            rows={4}
            className="dark-textarea"
          />
        </div>
      </div>

      {/* Bouton Dictée vocale */}
      {speechSupported && (
        <div className="form-section">
          <button
            type="button"
            className={`btn-dictate ${isListening ? 'active' : ''}`}
            onClick={toggleDictation}
          >
            {isListening ? <MicOff size={20} /> : <Mic size={20} />}
            {isListening ? ' Arrêter la dictée' : ' Dictée vocale'}
          </button>
          {isListening && (
            <span className="recording-indicator">🎤 Écoute en cours...</span>
          )}
        </div>
      )}

      {/* Audio */}
      <div className="form-section">
        <h3>Message vocal</h3>
        {!audioBlob ? (
          <button
            className={`btn-record ${isRecording ? 'recording' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
          >
            <Mic size={20} />
            {isRecording ? `Arrêter (${formatDuration(audioDuration)})` : 'Enregistrer'}
          </button>
        ) : (
          <div className="audio-playback">
            <audio controls src={URL.createObjectURL(audioBlob)} />
            <button className="btn-secondary" onClick={deleteRecording}>
              <RotateCcw size={16} /> Supprimer
            </button>
          </div>
        )}
      </div>

      {/* Actions principales */}
      <div className="form-actions">
        <button 
          className="btn-primary-purple btn-large"
          onClick={handleSendToNAS}
          disabled={sending}
        >
          {sending ? (
            <><Upload size={20} className="spin" /> Envoi en cours...</>
          ) : (
            <><Upload size={20} /> Envoyer sur NocoDB</>
          )}
        </button>

        <button className="btn-secondary" onClick={handleReset} disabled={sending}>
          <RotateCcw size={18} /> Réinitialiser
        </button>
      </div>
    </div>
  );
}

export default PriseDeCote;